/**
 * User Data Service
 * Centralized service for managing user-specific data
 * 
 * SUPABASE IS THE SINGLE SOURCE OF TRUTH
 * localStorage is only used as a short-term cache (fallback)
 * 
 * RULE: Every public function verifies auth via getVerifiedUserId()
 *       before touching the database.
 */

import { supabase } from './supabase';

// ============================================
// LOGGING HELPER
// ============================================
const log = (action, userId, details = '') => {
  console.log(`[UserData] ${action} | user: ${userId || 'NO_USER'} | ${details}`);
};

// ============================================
// AUTH VERIFICATION — call before EVERY DB read/write
// ============================================
/**
 * Returns the verified user ID from Supabase auth, or null.
 * This ensures the Supabase client has a valid JWT attached
 * for RLS-protected queries.  Without this, queries made
 * immediately after onAuthStateChange can silently return 0 rows.
 */
export const getVerifiedUserId = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) {
    log('AUTH_VERIFY', null, `Failed: ${error?.message || 'no user'}`);
    return null;
  }
  console.log("USER ID:", user.id);
  return user.id;
};

// ============================================
// CACHE HELPERS (Short-term only)
// ============================================
const getUserPrefix = (userId) => `pcos_${userId}_`;
const getCacheKey = (userId, dataType) => `${getUserPrefix(userId)}${dataType}`;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache

const getCachedData = (userId, dataType) => {
  if (!userId) return null;
  try {
    const cached = localStorage.getItem(getCacheKey(userId, dataType));
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (e) {}
  return null;
};

const setCachedData = (userId, dataType, data) => {
  if (!userId) return;
  try {
    localStorage.setItem(getCacheKey(userId, dataType), JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {}
};

// ============================================
// IN-MEMORY CACHES (all declared here so clearAllCaches can reference them)
// ============================================
const _profileCache = new Map();
const _profileCacheExpiry = new Map();
const _profileLocks = new Map();
const PROFILE_CACHE_TTL = 10000;
const _nameCache = new Map();
const _nameCacheExpiry = new Map();
const NAME_CACHE_TTL = 30000;
const _saveInProgress = new Map();

// ============================================
// CLEAR DATA (for logout)
// ============================================
export const clearAllPcosLocalStorage = () => {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('pcos')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));

  // Also clear Theo's in-session conversation cache so the next user
  // doesn't see the previous user's chat history
  try {
    const sKeys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('theo_session_conv_')) sKeys.push(key);
    }
    sKeys.forEach(k => sessionStorage.removeItem(k));
  } catch (_) {}

  console.log(`[UserData] LOGOUT: Cleared ${keysToRemove.length} cached items`);
};

export const clearUserLocalStorage = (userId) => {
  if (!userId) return;
  const prefix = getUserPrefix(userId);
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Clear ALL in-memory caches.  Must be called on logout so the
 * next login starts with a completely clean slate.
 */
export const clearAllCaches = () => {
  _profileCache.clear();
  _profileCacheExpiry.clear();
  _profileLocks.clear();
  _nameCache.clear();
  _nameCacheExpiry.clear();
  _saveInProgress.clear();
  console.log('[UserData] CACHES_CLEARED: All in-memory caches flushed');
};

// ============================================
// ONBOARDING - Check Supabase profiles table
// ============================================

// Profile deduplication using a lock mechanism
// This prevents "body stream already read" errors by ensuring only one request is made

const _fetchProfileOnce = async (userId) => {
  const now = Date.now();
  
  // 1. Check if we have valid cached data
  if (_profileCache.has(userId)) {
    const expiry = _profileCacheExpiry.get(userId) || 0;
    if (now < expiry) {
      log('PROFILE_CACHE_HIT', userId, 'Returning cached profile data');
      const cached = _profileCache.get(userId);
      return { data: cached.data, error: cached.error };
    }
    // Cache expired, clear it
    _profileCache.delete(userId);
    _profileCacheExpiry.delete(userId);
  }

  // 2. Check if there's already a request in progress (lock exists)
  if (_profileLocks.has(userId)) {
    log('PROFILE_WAIT', userId, 'Waiting for in-progress request to complete');
    // Wait for the lock to be released (cache to be populated)
    await new Promise(resolve => {
      const waiters = _profileLocks.get(userId) || [];
      waiters.push(resolve);
      _profileLocks.set(userId, waiters);
    });
    // Now the cache should be populated
    if (_profileCache.has(userId)) {
      const cached = _profileCache.get(userId);
      log('PROFILE_WAIT_DONE', userId, 'Got cached data after waiting');
      return { data: cached.data, error: cached.error };
    }
    // If still no cache, something went wrong - return empty
    log('PROFILE_WAIT_FAIL', userId, 'No cache after waiting, returning empty');
    return { data: null, error: null };
  }

  // 3. Acquire lock and make the request
  _profileLocks.set(userId, []);
  log('PROFILE_FETCH', userId, 'Fetching fresh profile from Supabase...');
  
  let cachedResult = { data: null, error: null };
  
  try {
    // Try to fetch profile - use a simpler query first to avoid column errors
    // The onboarding_completed column may not exist in the schema
    const result = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    
    // Extract only the fields we need, handling missing columns gracefully
    if (result.data) {
      cachedResult = {
        data: {
          onboarding_completed: result.data.onboarding_completed ?? null,
          age: result.data.age ?? null,
          diagnosis_status: result.data.diagnosis_status ?? null,
          cycle_regularity: result.data.cycle_regularity ?? null,
          primary_goal: result.data.primary_goal ?? null
        },
        error: null
      };
    } else if (result.error) {
      // Store error as plain object to avoid Response body issues
      cachedResult = {
        data: null,
        error: { message: String(result.error.message || 'Unknown error'), code: result.error.code }
      };
    }
    
    log('PROFILE_FETCH', userId, `Success: onboarding_completed=${cachedResult.data?.onboarding_completed}, age=${cachedResult.data?.age}`);
  } catch (err) {
    log('PROFILE_FETCH', userId, `Exception: ${err.message}`);
    cachedResult = { data: null, error: { message: String(err.message) } };
  }
  
  // Cache the result
  _profileCache.set(userId, cachedResult);
  _profileCacheExpiry.set(userId, Date.now() + PROFILE_CACHE_TTL);
  
  // Release lock and notify all waiters
  const waiters = _profileLocks.get(userId) || [];
  _profileLocks.delete(userId);
  waiters.forEach(resolve => resolve());
  log('PROFILE_LOCK_RELEASE', userId, `Released lock, notified ${waiters.length} waiters`);
  
  return cachedResult;
};

/**
 * Check if onboarding is complete.
 * 1. Verifies auth is ready (getUser)
 * 2. Checks profiles table for onboarding_completed flag
 * 3. Falls back to user_cycle_data / daily_checkins presence
 */
export const isOnboardingComplete = async (userId, options = {}) => {
  if (!userId) return false;
  
  log('CHECK_ONBOARDING', userId, 'Checking if user completed onboarding...');
  
  // ── verify auth is ready before any DB call ──
  // Skip verification if caller has already verified (e.g. App.handleUserLogin)
  // — saves a redundant getUser() round-trip during boot to prevent splash flicker.
  let uid = userId;
  if (!options.skipVerify) {
    const verifiedId = await getVerifiedUserId();
    if (!verifiedId) {
      log('CHECK_ONBOARDING', userId, 'Auth not ready — cannot check onboarding');
      return false;
    }
    uid = verifiedId;
  }
  
  try {
    const { data, error } = await _fetchProfileOnce(uid);
    
    if (error) {
      log('CHECK_ONBOARDING', uid, `Profile query error: ${error.message}`);
    }
    
    // Check 1: Profile has onboarding_completed flag
    if (data && data.onboarding_completed === true) {
      log('CHECK_ONBOARDING', uid, 'FOUND in Supabase - onboarding_completed=true');
      return true;
    }
    
    // Check 2: Profile has meaningful onboarding data
    if (data && (data.age || data.diagnosis_status || data.primary_goal)) {
      log('CHECK_ONBOARDING', uid, 'FOUND partial onboarding data in Supabase');
      return true;
    }
    
    // Check 3: Fallback — user_cycle_data row exists
    const { data: cycleRow, error: cycleErr } = await supabase
      .from('user_cycle_data')
      .select('user_id')
      .eq('user_id', uid)
      .maybeSingle();
    
    console.log("Fetched data:", cycleRow, "| table: user_cycle_data | user:", uid);
    
    if (cycleRow) {
      log('CHECK_ONBOARDING', uid, 'FOUND user data in user_cycle_data — treating as onboarded');
      return true;
    }
    
    // Check 4: daily_checkins row exists
    const { data: checkinRow } = await supabase
      .from('daily_checkins')
      .select('user_id')
      .eq('user_id', uid)
      .limit(1)
      .maybeSingle();
    
    if (checkinRow) {
      log('CHECK_ONBOARDING', uid, 'FOUND user data in daily_checkins — treating as onboarded');
      return true;
    }
    
    log('CHECK_ONBOARDING', uid, 'NOT FOUND — user needs onboarding');
    return false;
  } catch (err) {
    log('CHECK_ONBOARDING', uid, `Exception: ${err.message}`);
    return false;
  }
};

/**
 * Save onboarding data to Supabase using direct fetch to avoid body stream issues
 */
export const saveOnboardingData = async (userId, data) => {
  if (!userId || !data) return false;
  
  // Prevent concurrent saves
  const saveKey = `onboarding_${userId}`;
  if (_saveInProgress.has(saveKey)) {
    log('SAVE_ONBOARDING', userId, 'Save already in progress, skipping duplicate');
    return _saveInProgress.get(saveKey);
  }
  
  console.log("Saving data for user:", userId, "| table: profiles (onboarding) |", data);
  
  const savePromise = (async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      const makeXhrPatch = (body) => new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PATCH', `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('apikey', supabaseKey);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.setRequestHeader('Prefer', 'return=representation');
        xhr.onload = () => {
          let parsed = null;
          try { parsed = JSON.parse(xhr.responseText); } catch {}
          resolve({ status: xhr.status, data: parsed, text: xhr.responseText });
        };
        xhr.onerror = () => resolve({ status: 0, data: null, text: 'network error' });
        xhr.send(JSON.stringify(body));
      });
      
      // Attempt 1: Try saving all onboarding fields
      log('SAVE_ONBOARDING', userId, 'Attempt 1: Saving all fields...');
      let result = await makeXhrPatch({
        age: data.age ? parseInt(data.age) : null,
        diagnosis_status: data.diagnosis_status,
        cycle_regularity: data.cycle_regularity,
        primary_goal: data.primary_goal,
        onboarding_completed: true
      });
      
      if (result.status >= 200 && result.status < 300) {
        console.log("Fetched data:", result.data, "| user:", userId);
        _profileCache.delete(userId);
        _profileCacheExpiry.delete(userId);
        log('SAVE_ONBOARDING', userId, 'SUCCESS - all fields saved');
        return true;
      }
      
      // Attempt 2: If columns missing, just save onboarding_completed
      log('SAVE_ONBOARDING', userId, `Attempt 1 failed (${result.status}): ${result.text?.substring(0, 150)}. Trying minimal save...`);
      result = await makeXhrPatch({ onboarding_completed: true });
      
      if (result.status >= 200 && result.status < 300) {
        console.log("Fetched data:", result.data, "| user:", userId);
        _profileCache.delete(userId);
        _profileCacheExpiry.delete(userId);
        log('SAVE_ONBOARDING', userId, 'SUCCESS - onboarding_completed saved (some columns missing in profiles table)');
        return true;
      }
      
      log('SAVE_ONBOARDING', userId, `Attempt 2 also failed (${result.status}): ${result.text?.substring(0, 150)}`);
      
      // Attempt 3: Profiles columns don't exist - save a marker in user_cycle_data
      // so isOnboardingComplete fallback check finds this user
      log('SAVE_ONBOARDING', userId, 'Attempt 3: Saving marker to user_cycle_data...');
      const { error: markerErr } = await supabase
        .from('user_cycle_data')
        .upsert({
          user_id: userId,
          cycle_length: 28,
          phase: 'unknown',
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });
      
      if (!markerErr) {
        log('SAVE_ONBOARDING', userId, 'SUCCESS - saved marker to user_cycle_data (profiles columns missing - run migration SQL!)');
        console.log('WARNING: Profiles table is missing onboarding columns. Please run the ALTER TABLE migration from supabase_migration.sql');
        _profileCache.delete(userId);
        _profileCacheExpiry.delete(userId);
        return true;
      }
      
      log('SAVE_ONBOARDING', userId, `All attempts failed. Last error: ${markerErr?.message}`);
      return false;
    } catch (err) {
      log('SAVE_ONBOARDING', userId, `Exception: ${err.message}`);
      return false;
    } finally {
      _saveInProgress.delete(saveKey);
    }
  })();
  
  _saveInProgress.set(saveKey, savePromise);
  return savePromise;
};

/**
 * Mark onboarding as complete using XHR
 */
export const setOnboardingComplete = async (userId) => {
  if (!userId) return false;
  
  log('SET_ONBOARDING_COMPLETE', userId, 'Marking onboarding as complete...');
  
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    
    const status = await new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PATCH', `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('apikey', supabaseKey);
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      xhr.setRequestHeader('Prefer', 'return=minimal');
      xhr.onload = () => resolve(xhr.status);
      xhr.onerror = () => resolve(0);
      xhr.send(JSON.stringify({ onboarding_completed: true }));
    });
    
    if (status >= 200 && status < 300) {
      _profileCache.delete(userId);
      _profileCacheExpiry.delete(userId);
      log('SET_ONBOARDING_COMPLETE', userId, 'SUCCESS');
      return true;
    }
    
    log('SET_ONBOARDING_COMPLETE', userId, `Failed with status ${status}`);
    return false;
  } catch (err) {
    log('SET_ONBOARDING_COMPLETE', userId, `Exception: ${err.message}`);
    return false;
  }
};

/**
 * Fetch onboarding data from Supabase (deduplicated)
 */
export const fetchOnboardingData = async (userId) => {
  if (!userId) return null;
  
  log('FETCH_ONBOARDING', userId, 'Fetching from Supabase...');
  
  try {
    const { data, error } = await _fetchProfileOnce(userId);
    
    if (error) {
      log('FETCH_ONBOARDING', userId, `Error: ${error.message}`);
      return null;
    }
    
    if (data) {
      const result = {
        age: data.age,
        diagnosis_status: data.diagnosis_status,
        cycle_regularity: data.cycle_regularity,
        primary_goal: data.primary_goal,
        contact_number: data.contact_number || null,
        completed: data.onboarding_completed
      };
      console.log("Fetched data:", result, "| user:", userId);
      return result;
    }
    
    log('FETCH_ONBOARDING', userId, 'No data found');
    return null;
  } catch (err) {
    log('FETCH_ONBOARDING', userId, `Exception: ${err.message}`);
    return null;
  }
};

/**
 * Fetch user's name from Supabase profiles table
 */

export const fetchUserName = async (userId) => {
  if (!userId) return null;
  
  const now = Date.now();
  
  // Check cache
  if (_nameCache.has(userId)) {
    const expiry = _nameCacheExpiry.get(userId) || 0;
    if (now < expiry) {
      log('FETCH_NAME', userId, 'Returning cached name');
      return _nameCache.get(userId);
    }
  }
  
  log('FETCH_NAME', userId, 'Fetching name from Supabase...');
  
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('name')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      log('FETCH_NAME', userId, `Error: ${error.message}`);
      return null;
    }
    
    const name = data?.name || null;
    _nameCache.set(userId, name);
    _nameCacheExpiry.set(userId, now + NAME_CACHE_TTL);
    
    log('FETCH_NAME', userId, `Success: name=${name}`);
    return name;
  } catch (err) {
    log('FETCH_NAME', userId, `Exception: ${err.message}`);
    return null;
  }
};

// ============================================
// CYCLE DATA - Save to Supabase user_cycle_data
// ============================================

/**
 * Fetch cycle data from Supabase
 */
export const fetchCycleData = async (userId) => {
  if (!userId) return null;
  
  log('FETCH_CYCLE', userId, 'Fetching from Supabase...');
  
  // Check cache first (short-term fallback only)
  const cached = getCachedData(userId, 'cycle');
  if (cached) {
    log('FETCH_CYCLE', userId, 'Returning cached data (< 2 min old)');
    return cached;
  }
  
  try {
    const { data, error } = await supabase
      .from('user_cycle_data')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      log('FETCH_CYCLE', userId, `Error: ${error.message}`);
      return null;
    }
    
    if (data) {
      const cycleData = {
        lastPeriodDate: data.last_period_date,
        cycleLength: data.cycle_length,
        cycleDay: data.cycle_day,
        phase: data.phase
      };
      setCachedData(userId, 'cycle', cycleData);
      console.log("Fetched data:", cycleData, "| user:", userId);
      return cycleData;
    }
    
    log('FETCH_CYCLE', userId, 'No data found');
    return null;
  } catch (err) {
    log('FETCH_CYCLE', userId, `Exception: ${err.message}`);
    return null;
  }
};

/**
 * Save cycle data to Supabase, then immediately fetch & log result
 */
export const saveCycleData = async (userId, cycleData) => {
  if (!userId || !cycleData) return false;
  
  console.log("Saving data for user:", userId, "| table: user_cycle_data |", cycleData);
  
  try {
    const { error } = await supabase
      .from('user_cycle_data')
      .upsert({
        user_id: userId,
        last_period_date: cycleData.lastPeriodDate,
        cycle_length: cycleData.cycleLength || 28,
        cycle_day: cycleData.cycleDay,
        phase: cycleData.phase,
        updated_at: new Date().toISOString()
      }, { 
        onConflict: 'user_id'
      });
    
    if (error) {
      log('SAVE_CYCLE', userId, `Error: ${error.message}`);
      return false;
    }
    
    // Immediately fetch back from Supabase to verify persistence
    const { data: fetched, error: fetchErr } = await supabase
      .from('user_cycle_data')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    console.log("Fetched data:", fetched, fetchErr ? `| fetchError: ${fetchErr.message}` : '');
    
    // Update cache with verified data
    if (fetched) {
      const verified = {
        lastPeriodDate: fetched.last_period_date,
        cycleLength: fetched.cycle_length,
        cycleDay: fetched.cycle_day,
        phase: fetched.phase
      };
      setCachedData(userId, 'cycle', verified);
    }
    
    log('SAVE_CYCLE', userId, 'SUCCESS - saved & verified');
    return true;
  } catch (err) {
    log('SAVE_CYCLE', userId, `Exception: ${err.message}`);
    return false;
  }
};

// ============================================
// DAILY CHECK-INS - Save to Supabase daily_checkins
// ============================================

/**
 * Fetch today's check-in from Supabase
 */
export const fetchTodayCheckin = async (userId) => {
  if (!userId) return null;
  
  const today = new Date().toISOString().split('T')[0];
  log('FETCH_CHECKIN', userId, `Fetching for date=${today}`);
  
  // Check cache (short-term fallback)
  const cached = getCachedData(userId, `checkin_${today}`);
  if (cached) {
    log('FETCH_CHECKIN', userId, 'Returning cached data (< 2 min old)');
    return cached;
  }
  
  try {
    const { data, error } = await supabase
      .from('daily_checkins')
      .select('*')
      .eq('user_id', userId)
      .eq('checkin_date', today)
      .maybeSingle();
    
    if (error) {
      log('FETCH_CHECKIN', userId, `Error: ${error.message}`);
      return null;
    }
    
    if (data) {
      setCachedData(userId, `checkin_${today}`, data);
      console.log("Fetched data:", data, "| user:", userId);
      return data;
    }
    
    log('FETCH_CHECKIN', userId, 'No check-in found for today');
    return null;
  } catch (err) {
    log('FETCH_CHECKIN', userId, `Exception: ${err.message}`);
    return null;
  }
};

/**
 * Save daily check-in to Supabase, then immediately fetch & log result
 */
export const saveCheckin = async (userId, checkinData) => {
  if (!userId || !checkinData) return false;
  
  const today = new Date().toISOString().split('T')[0];
  console.log("Saving data for user:", userId, "| table: daily_checkins | date:", today, "|", checkinData);
  
  try {
    const { error } = await supabase
      .from('daily_checkins')
      .upsert({
        user_id: userId,
        checkin_date: today,
        symptoms: checkinData.symptoms || checkinData,
        mood: checkinData.mood,
        energy_level: checkinData.energy_level || checkinData.energy,
        pain_level: checkinData.pain_level || checkinData.pain,
        notes: checkinData.notes,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,checkin_date'
      });
    
    if (error) {
      log('SAVE_CHECKIN', userId, `Error: ${error.message}`);
      return false;
    }
    
    // Immediately fetch back from Supabase to verify persistence
    const { data: fetched, error: fetchErr } = await supabase
      .from('daily_checkins')
      .select('*')
      .eq('user_id', userId)
      .eq('checkin_date', today)
      .maybeSingle();
    
    console.log("Fetched data:", fetched, fetchErr ? `| fetchError: ${fetchErr.message}` : '');
    
    // Update cache with verified data
    if (fetched) {
      setCachedData(userId, `checkin_${today}`, fetched);
    }
    
    log('SAVE_CHECKIN', userId, 'SUCCESS - saved & verified');
    return true;
  } catch (err) {
    log('SAVE_CHECKIN', userId, `Exception: ${err.message}`);
    return false;
  }
};

/**
 * Fetch weekly check-in count
 */
export const fetchWeeklyCheckinCount = async (userId) => {
  if (!userId) return 0;
  
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);
  
  try {
    const { data, error, count } = await supabase
      .from('daily_checkins')
      .select('id', { count: 'exact' })
      .eq('user_id', userId)
      .gte('checkin_date', weekAgo.toISOString().split('T')[0])
      .lte('checkin_date', today.toISOString().split('T')[0]);
    
    if (error) {
      log('FETCH_WEEKLY_CHECKINS', userId, `Error: ${error.message}`);
      return 0;
    }
    
    log('FETCH_WEEKLY_CHECKINS', userId, `Count: ${count || data?.length || 0}`);
    return count || data?.length || 0;
  } catch (err) {
    return 0;
  }
};

// ============================================
// DAILY HABITS - Save to Supabase daily_habits
// ============================================

/**
 * Fetch today's habits from Supabase
 */
export const fetchTodayHabits = async (userId) => {
  if (!userId) return null;
  
  const today = new Date().toISOString().split('T')[0];
  log('FETCH_HABITS', userId, `Fetching for date=${today}`);
  
  // Check cache (short-term fallback)
  const cached = getCachedData(userId, `habits_${today}`);
  if (cached) {
    log('FETCH_HABITS', userId, 'Returning cached data (< 2 min old)');
    return cached;
  }
  
  try {
    const { data, error } = await supabase
      .from('daily_habits')
      .select('*')
      .eq('user_id', userId)
      .eq('habit_date', today)
      .maybeSingle();
    
    if (error) {
      log('FETCH_HABITS', userId, `Error: ${error.message}`);
      return null;
    }
    
    if (data) {
      const habits = {
        water: data.water || false,
        exercise: data.exercise || false,
        sleep: data.sleep || false,
        supplements: data.supplements || false
      };
      setCachedData(userId, `habits_${today}`, habits);
      console.log("Fetched data:", habits, "| user:", userId);
      return habits;
    }
    
    log('FETCH_HABITS', userId, 'No habits found for today');
    return null;
  } catch (err) {
    log('FETCH_HABITS', userId, `Exception: ${err.message}`);
    return null;
  }
};

/**
 * Save habits to Supabase, then immediately fetch & log result
 */
export const saveHabits = async (userId, habitsData) => {
  if (!userId || !habitsData) return false;
  
  const today = new Date().toISOString().split('T')[0];
  console.log("Saving data for user:", userId, "| table: daily_habits | date:", today, "|", habitsData);
  
  try {
    const { error } = await supabase
      .from('daily_habits')
      .upsert({
        user_id: userId,
        habit_date: today,
        water: habitsData.water || false,
        exercise: habitsData.exercise || false,
        sleep: habitsData.sleep || false,
        supplements: habitsData.supplements || false,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,habit_date'
      });
    
    if (error) {
      log('SAVE_HABITS', userId, `Error: ${error.message}`);
      return false;
    }
    
    // Immediately fetch back from Supabase to verify persistence
    const { data: fetched, error: fetchErr } = await supabase
      .from('daily_habits')
      .select('*')
      .eq('user_id', userId)
      .eq('habit_date', today)
      .maybeSingle();
    
    console.log("Fetched data:", fetched, fetchErr ? `| fetchError: ${fetchErr.message}` : '');
    
    // Update cache with verified data
    if (fetched) {
      const verified = {
        water: fetched.water,
        exercise: fetched.exercise,
        sleep: fetched.sleep,
        supplements: fetched.supplements
      };
      setCachedData(userId, `habits_${today}`, verified);
    }
    
    log('SAVE_HABITS', userId, 'SUCCESS - saved & verified');
    return true;
  } catch (err) {
    log('SAVE_HABITS', userId, `Exception: ${err.message}`);
    return false;
  }
};

// ============================================
// HORMONE DATA - Uses existing hormone_reports table
// ============================================

/**
 * Fetch hormone data from Supabase
 */
export const fetchHormoneData = async (userId) => {
  if (!userId) return null;
  
  log('FETCH_HORMONES', userId, 'Fetching from Supabase...');
  
  const cached = getCachedData(userId, 'hormones');
  if (cached) {
    log('FETCH_HORMONES', userId, 'Returning cached data (< 2 min old)');
    return cached;
  }
  
  try {
    const { data, error } = await supabase
      .from('hormone_reports')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error) {
      log('FETCH_HORMONES', userId, `Error: ${error.message}`);
      return null;
    }
    
    if (data) {
      const hormoneData = {
        lh: data.lh,
        fsh: data.fsh,
        insulin: data.insulin,
        testosterone: data.testosterone || data.testosteone,
        amh: data.amh,
        lhFshRatio: data.lhFshRatio || (data.lh && data.fsh ? (data.lh / data.fsh).toFixed(2) : null),
        createdAt: data.created_at
      };
      setCachedData(userId, 'hormones', hormoneData);
      console.log("Fetched data:", hormoneData, "| user:", userId);
      return hormoneData;
    }
    
    log('FETCH_HORMONES', userId, 'No hormone data found');
    return null;
  } catch (err) {
    log('FETCH_HORMONES', userId, `Exception: ${err.message}`);
    return null;
  }
};

/**
 * Fetch hormone history
 */
export const fetchHormoneHistory = async (userId) => {
  if (!userId) return [];
  
  try {
    const { data, error } = await supabase
      .from('hormone_reports')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10);
    
    if (error) {
      log('FETCH_HORMONE_HISTORY', userId, `Error: ${error.message}`);
      return [];
    }
    
    log('FETCH_HORMONE_HISTORY', userId, `Found ${data?.length || 0} records`);
    return data || [];
  } catch (err) {
    return [];
  }
};

// ============================================
// SYMPTOM DATA - Uses existing symptom_logs table
// ============================================

/**
 * Fetch symptom logs from Supabase
 */
export const fetchSymptomLogs = async (userId, limit = 30) => {
  if (!userId) return [];
  
  log('FETCH_SYMPTOMS', userId, `Fetching last ${limit} logs...`);
  
  try {
    const { data, error } = await supabase
      .from('symptom_logs')
      .select('*')
      .eq('user_id', userId)
      .order('log_date', { ascending: false })
      .limit(limit);
    
    if (error) {
      log('FETCH_SYMPTOMS', userId, `Error: ${error.message}`);
      return [];
    }
    
    log('FETCH_SYMPTOMS', userId, `SUCCESS: Found ${data?.length || 0} logs`);
    return data || [];
  } catch (err) {
    log('FETCH_SYMPTOMS', userId, `Exception: ${err.message}`);
    return [];
  }
};

/**
 * Calculate symptom pattern from logs
 */
export const fetchSymptomPattern = async (userId) => {
  if (!userId) return null;
  
  const logs = await fetchSymptomLogs(userId, 30);
  
  if (logs.length === 0) return null;
  
  const symptoms = ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'];
  const pattern = { averages: {} };
  
  symptoms.forEach(symptom => {
    const values = logs.map(l => l[symptom]).filter(v => v !== undefined && v !== null);
    if (values.length > 0) {
      pattern.averages[symptom] = values.reduce((a, b) => a + b, 0) / values.length;
    }
  });
  
  return Object.keys(pattern.averages).length > 0 ? pattern : null;
};

// ============================================
// PERIOD/BLEEDING HISTORY
// ============================================

/**
 * Fetch period history
 */
export const fetchPeriodHistory = async (userId) => {
  if (!userId) return [];
  
  try {
    const { data, error } = await supabase
      .from('period_history')
      .select('*')
      .eq('user_id', userId)
      .order('start_date', { ascending: false })
      .limit(12);
    
    if (error) {
      log('FETCH_PERIOD_HISTORY', userId, `Error: ${error.message}`);
      return [];
    }
    
    log('FETCH_PERIOD_HISTORY', userId, `Found ${data?.length || 0} records`);
    return data || [];
  } catch (err) {
    return [];
  }
};

/**
 * Save period entry, then immediately fetch & log result
 */
export const savePeriodEntry = async (userId, periodData) => {
  if (!userId || !periodData) return false;
  
  console.log("Saving data for user:", userId, "| table: period_history |", periodData);
  
  try {
    const { error } = await supabase
      .from('period_history')
      .insert({
        user_id: userId,
        start_date: periodData.start_date,
        end_date: periodData.end_date,
        duration: periodData.duration,
        flow_intensity: periodData.flow_intensity,
        notes: periodData.notes
      });
    
    if (error) {
      log('SAVE_PERIOD', userId, `Error: ${error.message}`);
      return false;
    }
    
    // Immediately fetch back the latest entry to verify
    const { data: fetched, error: fetchErr } = await supabase
      .from('period_history')
      .select('*')
      .eq('user_id', userId)
      .order('start_date', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    console.log("Fetched data:", fetched, fetchErr ? `| fetchError: ${fetchErr.message}` : '');
    
    log('SAVE_PERIOD', userId, 'SUCCESS - saved & verified');
    return true;
  } catch (err) {
    log('SAVE_PERIOD', userId, `Exception: ${err.message}`);
    return false;
  }
};

/**
 * Fetch bleeding history
 */
export const fetchBleedingHistory = async (userId) => {
  if (!userId) return [];
  
  try {
    const { data, error } = await supabase
      .from('bleeding_history')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(90);
    
    if (error) return [];
    return data || [];
  } catch (err) {
    return [];
  }
};

// ============================================
// AGGREGATED DATA FETCH
// ============================================

/**
 * Fetch all user data at once
 */
export const fetchAllUserData = async (userId) => {
  if (!userId) return null;
  
  log('FETCH_ALL', userId, 'Loading all user data from Supabase...');
  
  const [
    onboardingData,
    cycleData,
    hormoneData,
    symptomLogs,
    todayCheckin,
    todayHabits
  ] = await Promise.all([
    fetchOnboardingData(userId),
    fetchCycleData(userId),
    fetchHormoneData(userId),
    fetchSymptomLogs(userId),
    fetchTodayCheckin(userId),
    fetchTodayHabits(userId)
  ]);
  
  log('FETCH_ALL', userId, 'COMPLETE');
  
  return {
    onboardingData,
    cycleData,
    hormoneData,
    symptomLogs,
    todayCheckin,
    todayHabits,
    symptomPattern: await fetchSymptomPattern(userId)
  };
};
